import { Box, VStack, Spinner, useBreakpointValue, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ContentRow from '../components/ContentRow';
import HeroBanner from '../components/HeroBanner';
import { getContent, getTopContent, getRecentUploads, getContinueWatching, getSubscribedCreators, type ContentItem, type ContinueWatchingItem, type SubscribedCreator } from '../services/contentService';
import { useAuth } from '../contexts/AuthContext';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const [loading, setLoading] = useState(true);
  const [continueWatching, setContinueWatching] = useState<ContinueWatchingItem[]>([]);
  const [subscribedCreators, setSubscribedCreators] = useState<SubscribedCreator[]>([]);
  const [topContent, setTopContent] = useState<ContentItem[]>([]);
  const [recentUploads, setRecentUploads] = useState<ContentItem[]>([]);
  const [movies, setMovies] = useState<ContentItem[]>([]);
  const [series, setSeries] = useState<ContentItem[]>([]);
  const [shorts, setShorts] = useState<ContentItem[]>([]);
  const [comics, setComics] = useState<ContentItem[]>([]);
  const [novels, setNovels] = useState<ContentItem[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load all content in parallel
        const [
          topContentData,
          recentUploadsData,
          moviesData,
          seriesData,
          shortsData,
          comicsData,
          novelsData
        ] = await Promise.all([
          getTopContent('all', 'week', 20),
          getRecentUploads(20),
          getContent('movie', 20),
          getContent('series', 20),
          getContent('short', 20),
          getContent('comic', 20),
          getContent('novel', 20)
        ]);
        
        setTopContent(topContentData);
        setRecentUploads(recentUploadsData);
        setMovies(moviesData);
        setSeries(seriesData);
        setShorts(shortsData);
        setComics(comicsData);
        setNovels(novelsData);
        
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast({
          title: 'Error loading content',
          description: 'Unable to load dashboard data. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [toast]);

  // Set up real-time listeners for user-specific data
  useEffect(() => {
    if (!user) return;

    const unsubscribeContinueWatching = getContinueWatching(user.uid, setContinueWatching);
    const unsubscribeSubscribedCreators = getSubscribedCreators(user.uid, setSubscribedCreators);

    return () => {
      unsubscribeContinueWatching();
      unsubscribeSubscribedCreators();
    };
  }, [user]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" color="purple.400" />
      </Box>
    );
  }

  const contentRows = [
    // Continue Watching (only show if user has data)
    ...(continueWatching.length > 0 ? [{
      title: 'Continue Watching',
      items: continueWatching.map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail,
        progress: item.progress,
        episode: item.currentEpisode ? `Episode ${item.currentEpisode}` : undefined,
        season: item.currentSeason ? `Season ${item.currentSeason}` : undefined,
        duration: item.duration ? `${item.duration} min` : undefined
      })),
      isContinueWatching: true
    }] : []),
    
    // Followed Creators (only show if user has follows)
    ...(subscribedCreators.length > 0 ? [{
      title: 'Followed Creators',
      items: subscribedCreators.map(creator => ({
        id: creator.id,
        title: creator.name,
        type: 'creator',
        thumbnail: creator.avatar || '',
        avatar: creator.avatar,
        followers: creator.followers,
        newContent: creator.newContentCount,
        isOnline: creator.isOnline,
        lastActive: creator.lastActive.toLocaleDateString(),
        verified: creator.verified
      })),
      isSubscribed: true
    }] : []),
    
    // Top content sections
    {
      title: 'Top 50 this week',
      items: topContent.slice(0, 6).map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail
      }))
    },
    {
      title: 'Top 50 this month',
      items: topContent.slice(6, 12).map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail
      }))
    },
    {
      title: 'Recent Uploads',
      items: recentUploads.slice(0, 6).map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail
      }))
    },
    {
      title: 'Top 50 Movies',
      items: movies.slice(0, 6).map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail
      }))
    },
    {
      title: 'Top 50 Series',
      items: series.slice(0, 6).map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail
      }))
    },
    {
      title: 'Top 50 Shorts',
      items: shorts.slice(0, 6).map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail
      }))
    },
    {
      title: 'Top 50 Comics',
      items: comics.slice(0, 6).map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail
      }))
    },
    {
      title: 'Top 50 Novels',
      items: novels.slice(0, 6).map(item => ({
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail
      }))
    }
  ];

  const handleSeeAll = (title: string) => {
    const routeMap: { [key: string]: string } = {
      'Top 50 Movies': '/movies',
      'Top 50 Series': '/series',
      'Top 50 Shorts': '/shorts',
      'Top 50 Comics': '/comics',
      'Top 50 Novels': '/novels',
      'Recent Uploads': '/search'
    };
    
    const route = routeMap[title] || '/search';
    navigate(route);
  };

  return (
    <VStack gap={8} align="stretch" maxW="1400px" mx="auto" px={isMobile ? 4 : 8}>
      <HeroBanner />
      
      <VStack gap={6} align="stretch">
        {contentRows.map((row, index) => (
          <ContentRow
            key={index}
            title={row.title}
            items={row.items}
            onSeeAll={() => handleSeeAll(row.title)}
            isContinueWatching={row.isContinueWatching}
            isSubscribed={row.isSubscribed}
          />
        ))}
      </VStack>
    </VStack>
  );
};

export default Dashboard;